// src/pages/AdminSubscriptions.jsx
// Emails réels via la RPC admin-gated admin_list_users() (jamais auth.admin.* côté client).

import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

export default function AdminSubscriptions() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('tous');

  const ROLES = [
    { value: 'tous', label: 'Tous', icon: '📊' },
    { value: 'user', label: 'Utilisateur', icon: '👤' },
    { value: 'expert', label: 'Expert', icon: '👨‍🎨' },
    { value: 'family_office', label: 'Family Office', icon: '🏛️' },
    { value: 'team', label: 'Équipe', icon: '👥' },
    { value: 'admin', label: 'Admin', icon: '⚙️' }
  ];

  const PLANS = [
    { value: 'standard', label: 'Standard', color: 'bg-blue-100 text-blue-800' },
    { value: 'premium', label: 'Premium', color: 'bg-purple-100 text-purple-800' },
    { value: 'art_advisor', label: 'Art Advisor', color: 'bg-amber-100 text-amber-800' },
    { value: 'art_advisor_premium', label: 'Art Advisor Premium', color: 'bg-amber-100 text-amber-800' },
    { value: 'family_office', label: 'Family Office', color: 'bg-teal-100 text-teal-800' },
    { value: 'fondation', label: 'Fondation', color: 'bg-rose-100 text-rose-800' }
  ];

  const STATUSES = [
    { value: 'active', label: 'Actif', color: 'bg-green-100 text-green-800' },
    { value: 'unpaid', label: 'Impayé', color: 'bg-red-100 text-red-800' },
    { value: 'canceled', label: 'Annulé', color: 'bg-gray-100 text-gray-800' },
    { value: null, label: 'Non défini', color: 'bg-gray-100 text-gray-500' }
  ];

  useEffect(() => {
    fetchProfiles();
  }, []);

  async function fetchProfiles() {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 AdminSubscriptions: Récupération des profils...');

      // Récupérer tous les profils avec les informations d'abonnement
      const { data, error: profileError } = await supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          company,
          role,
          plan,
          plan_started_at,
          stripe_subscription_status,
          stripe_customer_id,
          free_until,
          created_at,
          approved,
          is_demo,
          foundation_mode,
          gallery_mode
        `)
        .order('created_at', { ascending: false });

      if (profileError) {
        console.error('❌ Erreur profiles:', profileError);
        throw profileError;
      }

      console.log(`✅ ${data.length} profils récupérés`);

      // Enrichir avec l'email réel via la RPC admin-gated (auth.users n'est pas
      // lisible côté client). Non bloquant : si elle échoue, on garde les profils.
      let emailsById = {};
      try {
        const { data: rows } = await supabase.rpc('admin_list_users');
        (rows || []).forEach((r) => { if (r.email) emailsById[r.id] = r.email; });
      } catch (e) {
        console.warn('admin_list_users indisponible, emails masqués:', e);
      }

      setProfiles(data.map((p) => ({ ...p, email: emailsById[p.id] || null })));
    } catch (err) {
      console.error('❌ Erreur dans fetchProfiles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function getStatusInfo(status) {
    return STATUSES.find(s => s.value === status) || STATUSES[STATUSES.length - 1];
  }

  function getPlanInfo(plan) {
    return PLANS.find(p => p.value === plan) || { label: plan || 'Non défini', color: 'bg-gray-100 text-gray-500' };
  }

  function formatDate(dateString) {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  const filteredProfiles = activeFilter === 'tous' 
    ? profiles 
    : profiles.filter(p => p.role === activeFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des abonnements...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <span className="text-2xl">❌</span>
          <div>
            <h3 className="font-semibold text-red-900 mb-2">Erreur de chargement</h3>
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
              <p className="text-sm text-yellow-800 mb-2">
                <strong>💡 Solution :</strong> Cette erreur indique un problème de permissions RLS.
              </p>
              <p className="text-xs text-yellow-700">
                Exécutez le script <code className="bg-yellow-100 px-1 rounded">fix-admin-permissions.sql</code> dans Supabase SQL Editor.
              </p>
            </div>
            <button 
              onClick={fetchProfiles}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-bold text-gray-900 flex items-center gap-2">
            📊 Abonnements des utilisateurs
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {filteredProfiles.length} abonnement{filteredProfiles.length > 1 ? 's' : ''} • Vue d'ensemble des plans
          </p>
        </div>
        <button 
          onClick={fetchProfiles}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualiser
        </button>
      </div>

      {/* Filtres par rôle */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 flex-wrap">
          {ROLES.map(role => {
            const count = role.value === 'tous' 
              ? profiles.length 
              : profiles.filter(p => p.role === role.value).length;
            
            return (
              <button
                key={role.value}
                onClick={() => setActiveFilter(role.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeFilter === role.value
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="mr-2">{role.icon}</span>
                {role.label}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeFilter === role.value 
                    ? 'bg-white/20 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
          <div className="text-3xl font-bold text-blue-900">
            {profiles.filter(p => p.plan === 'standard').length}
          </div>
          <div className="text-sm text-blue-700 mt-1">Plans Standard</div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
          <div className="text-3xl font-bold text-purple-900">
            {profiles.filter(p => p.plan === 'premium').length}
          </div>
          <div className="text-sm text-purple-700 mt-1">Plans Premium</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
          <div className="text-3xl font-bold text-green-900">
            {profiles.filter(p => p.stripe_subscription_status === 'active').length}
          </div>
          <div className="text-sm text-green-700 mt-1">Abonnements actifs</div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 border border-amber-200">
          <div className="text-3xl font-bold text-amber-900">
            {profiles.filter(p => p.free_until && new Date(p.free_until) > new Date()).length}
          </div>
          <div className="text-sm text-amber-700 mt-1">Accès gratuits</div>
        </div>
      </div>

      {/* Table */}
      {filteredProfiles.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <span className="text-6xl mb-4 block">📭</span>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun abonnement trouvé</h3>
          <p className="text-gray-600">
            {activeFilter === 'tous' 
              ? "Il n'y a actuellement aucun abonnement dans la base de données."
              : `Aucun abonnement pour le rôle "${ROLES.find(r => r.value === activeFilter)?.label}".`
            }
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                    Utilisateur
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                    Rôle
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                    Plan
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                    Statut
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                    Début
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                    Gratuit jusqu'au
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProfiles.map((profile) => {
                  const statusInfo = profile.is_demo
                    ? { label: 'Démo', color: 'bg-indigo-100 text-indigo-700' }
                    : getStatusInfo(profile.stripe_subscription_status);
                  const planInfo = getPlanInfo(profile.plan);
                  const roleInfo = profile.foundation_mode
                    ? { icon: '🏛️', label: 'Fondation' }
                    : profile.gallery_mode
                    ? { icon: '🖼️', label: 'Galerie' }
                    : (ROLES.find(r => r.value === profile.role) || { icon: '❓', label: profile.role });
                  
                  return (
                    <tr key={profile.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {profile.first_name && profile.last_name
                              ? `${profile.first_name} ${profile.last_name}`
                              : profile.first_name || profile.company || 'Non défini'}
                          </span>
                          {profile.email && (
                            <span className="text-xs text-gray-600 break-all">{profile.email}</span>
                          )}
                          <span className="text-xs text-gray-400 font-mono">{profile.id.substring(0, 8)}...</span>
                          {profile.company && profile.first_name && (
                            <span className="text-xs text-gray-500">{profile.company}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm">
                          {roleInfo.icon} {roleInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${planInfo.color}`}>
                          {planInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(profile.plan_started_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {profile.free_until ? (
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-green-700">
                              {formatDate(profile.free_until)}
                            </span>
                            {new Date(profile.free_until) > new Date() && (
                              <span className="text-xs text-green-600">✓ Actif</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}